import type { ComponentType } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useStreamSelector } from "../../useStreamSelector";
import { StreamSelectors } from "../../StreamSelectors";
import { useComposerContext } from "../../composer/ComposerProvider";
import type { RecommendationSubmissionProps } from "./types";
import { useComposerSelectorWithManager } from "../../composer/useComposerSelector";
import type { ComposerAttachment, CompletedAttachment } from "../../composer/types";
import {
  attachmentTag,
  attachmentTagPrefix,
  buildRecommendationSubmission,
} from "./buildSubmission";
import { parseRecommendationFields } from "./parseCollects";
import type {
  Recommendation,
  RecommendationPanelDescriptor,
  RecommendationPanelRenderers,
  CollectedValue,
  CollectedValues,
  ParsedRecommendationField,
} from "./types";

// ─────────────────────────────────────────────────────────────────────────
// Context shape
// ─────────────────────────────────────────────────────────────────────────

export interface RecommendationsContextValue {
  recommendations: Recommendation[];
  renderers: RecommendationPanelRenderers;
  /**
   * Transcript card for panel submissions with no bespoke `Submission`
   * renderer — mirrors the auto-generated field panel (prompts + values +
   * note). Prompt clicks never use it; they stay plain bubbles.
   */
  defaultSubmission?: ComponentType<RecommendationSubmissionProps>;
  active: Recommendation | null;
  /** Expand a recommendation's panel (the composer transforms). */
  open: (rec: Recommendation) => void;
  /** Collapse back to chat, KEEPING field state ("Back to chat" / esc). */
  close: () => void;
  /** Discard the active panel's fields + tagged attachments, then close. */
  cancel: () => void;
  /**
   * A prompt recommendation (no panel) PREFILLS the chat input with its
   * text — the user edits and sends it themselves (never auto-sends).
   */
  prefillPrompt: (rec: Recommendation) => void;
  /** Assemble the active panel + composer into one message and send. */
  submitActive: () => void;
  /** Whether the active panel can submit (required fields, uploads settled). */
  isSubmittable: boolean;
  descriptorFor: (rec: Recommendation) => RecommendationPanelDescriptor | undefined;
  /** App-provided icon resolver (e.g. registry icon name → lucide node). */
  iconFor?: (rec: Recommendation) => ReactNode;
  /** A recommendation opens a panel iff it has a bespoke Panel or collects. */
  hasPanel: (rec: Recommendation) => boolean;
  isStreaming: boolean;
  // Active-panel field state (keyed by the active recommendation)
  fields: ParsedRecommendationField[];
  collected: CollectedValues;
  setField: (field: string, value: CollectedValue) => void;
  addImages: (field: string, files: FileList | File[], label?: string) => void;
  attachmentsFor: (field: string) => ComposerAttachment[];
  removeAttachment: (id: string) => void;
  submitLabel?: ReactNode;
  notePlaceholder?: string;
}

const RecommendationsContext = createContext<RecommendationsContextValue | null>(null);

export function useRecommendations(): RecommendationsContextValue {
  const ctx = useContext(RecommendationsContext);
  if (!ctx) {
    throw new Error(
      "useRecommendations must be used within <Recommendations.Root>",
    );
  }
  return ctx;
}

/** Renderer-mapping access that tolerates missing Root (transcript views). */
export function useRecommendationRenderers(): RecommendationPanelRenderers {
  return useContext(RecommendationsContext)?.renderers ?? {};
}

/**
 * The bespoke-panel hook — everything a panel body needs, without prop
 * threading. Same values are also passed to the renderer as props.
 */
export function useRecommendationPanel() {
  const {
    active,
    fields,
    collected,
    setField,
    addImages,
    attachmentsFor,
    removeAttachment,
    submitActive,
    cancel,
  } = useRecommendations();
  if (!active) {
    throw new Error("useRecommendationPanel: no recommendation panel is active");
  }
  return {
    recommendation: active,
    fields,
    collected,
    setField,
    addImages,
    attachmentsFor,
    removeAttachment,
    submit: submitActive,
    cancel,
  };
}

// ─────────────────────────────────────────────────────────────────────────
// Root — stream-connected by default; standalone with explicit props
// ─────────────────────────────────────────────────────────────────────────

export interface RecommendationsRootProps {
  children: ReactNode;
  /** App-injected mapping: component name (or recommendationId) → panel. */
  renderers?: RecommendationPanelRenderers;
  /** Transcript card for panel submissions without a bespoke Submission. */
  defaultSubmission?: ComponentType<RecommendationSubmissionProps>;
  /**
   * Override the recommendations list. Default: the stream's
   * `values.recommendations` channel (requires being inside Chat.Root).
   */
  recommendations?: Recommendation[];
  /**
   * Override where submissions go (stories/tests). Default: the stream's
   * submit (which runs any registered SubmitMiddleware). Providing BOTH
   * `recommendations` and `onSubmit` makes Root standalone — usable
   * outside Chat.Root (still requires a ComposerProvider).
   */
  onSubmit?: (payload: Record<string, unknown>) => void;
  /**
   * Decorate the submit payload (e.g. attach the active page route the way
   * the surface's composer formatSubmit does). Applied to panel AND prompt
   * submissions.
   */
  decorateSubmit?: (payload: Record<string, unknown>) => Record<string, unknown>;
  /** Resolve a chip icon (e.g. the registry's icon name → a lucide node). */
  iconFor?: (rec: Recommendation) => ReactNode;
  /** Send button content (e.g. "Send to Houston"). Default "Send". */
  submitLabel?: ReactNode;
  /** Placeholder for the persistent note. Default "Add a note… (optional)". */
  notePlaceholder?: string;
}

export function RecommendationsRoot(props: RecommendationsRootProps) {
  const standalone = props.recommendations !== undefined && props.onSubmit !== undefined;
  return standalone ? (
    <RootInner
      {...props}
      streamRecommendations={undefined}
      streamSubmit={undefined}
      isStreaming={false}
    />
  ) : (
    <StreamConnectedRoot {...props} />
  );
}

function StreamConnectedRoot(props: RecommendationsRootProps) {
  const streamRecommendations = useStreamSelector(
    (s) => (s.values as { recommendations?: Recommendation[] } | undefined)?.recommendations,
  );
  const streamSubmit = useStreamSelector((s) => s.submit);
  const isStreaming = useStreamSelector(StreamSelectors.isStreaming);
  return (
    <RootInner
      {...props}
      streamRecommendations={streamRecommendations}
      streamSubmit={streamSubmit}
      isStreaming={isStreaming}
    />
  );
}

interface RootInnerProps extends RecommendationsRootProps {
  streamRecommendations: Recommendation[] | undefined;
  streamSubmit: ((payload: Record<string, unknown>) => unknown) | undefined;
  isStreaming: boolean;
}

function RootInner({
  children,
  renderers = {},
  defaultSubmission,
  recommendations: recommendationsProp,
  onSubmit,
  decorateSubmit,
  iconFor,
  submitLabel,
  notePlaceholder,
  streamRecommendations,
  streamSubmit,
  isStreaming,
}: RootInnerProps) {
  const composer = useComposerContext();
  const [active, setActive] = useState<Recommendation | null>(null);
  // Field state keyed by recommendationId — survives collapse/reopen.
  const [collectedByRec, setCollectedByRec] = useState<
    Record<string, CollectedValues>
  >({});

  // Subscribe to composer attachments so attachmentsFor/isSubmittable track uploads.
  const attachments = useComposerSelectorWithManager(composer, (s) => s.attachments);
  // The note is still chat: typed text makes the panel sendable even with
  // required fields empty — the agent picks up the ask conversationally
  // ("can you find some stock photos?" on an empty gallery).
  const noteText = useComposerSelectorWithManager(composer, (s) => s.text);

  const recommendations = recommendationsProp ?? streamRecommendations ?? [];

  const send = useCallback(
    (payload: Record<string, unknown>) => {
      const decorated = decorateSubmit ? decorateSubmit(payload) : payload;
      if (onSubmit) onSubmit(decorated);
      else streamSubmit?.(decorated);
    },
    [decorateSubmit, onSubmit, streamSubmit],
  );

  const descriptorFor = useCallback(
    (rec: Recommendation): RecommendationPanelDescriptor | undefined =>
      (rec.component ? renderers[rec.component] : undefined) ??
      renderers[rec.recommendationId],
    [renderers],
  );

  const hasPanel = useCallback(
    (rec: Recommendation): boolean =>
      Boolean(descriptorFor(rec)?.Panel) ||
      parseRecommendationFields(rec.collects).length > 0,
    [descriptorFor],
  );

  const open = useCallback((rec: Recommendation) => {
    // A pinned id means the composer text is a prior prompt chip's
    // prefill, not something the user typed. Activating this panel
    // replaces that suggestion — discard both (user-typed text, with no
    // pin, stays and becomes the panel's note).
    if (composer.pendingMetadata) composer.setText("");
    // Seed field defaults (choice controls) so an untouched panel still
    // submits them — without clobbering state from a collapse/reopen.
    const defaults = parseRecommendationFields(rec.collects).filter(
      (f) => f.default !== undefined,
    );
    if (defaults.length > 0) {
      setCollectedByRec((prev) => {
        const existing = prev[rec.recommendationId] ?? {};
        const seeded = { ...existing };
        for (const f of defaults) {
          if (seeded[f.name] === undefined) seeded[f.name] = f.default as string;
        }
        return { ...prev, [rec.recommendationId]: seeded };
      });
    }
    setActive(rec);
  }, [composer]);
  const close = useCallback(() => setActive(null), []);

  const cancel = useCallback(() => {
    if (active) {
      const prefix = attachmentTagPrefix(active.recommendationId);
      composer.removeAttachments((a) => Boolean(a.tag?.startsWith(prefix)));
      const recId = active.recommendationId;
      setCollectedByRec((prev) => {
        const next = { ...prev };
        delete next[recId];
        return next;
      });
    }
    setActive(null);
  }, [active, composer]);

  const collected = (active && collectedByRec[active.recommendationId]) || {};
  const fields = useMemo(
    () => parseRecommendationFields(active?.collects),
    [active],
  );

  const setField = useCallback(
    (field: string, value: CollectedValue) => {
      if (!active) return;
      const recId = active.recommendationId;
      setCollectedByRec((prev) => ({
        ...prev,
        [recId]: { ...(prev[recId] ?? {}), [field]: value },
      }));
    },
    [active],
  );

  const addImages = useCallback(
    (field: string, files: FileList | File[], label?: string) => {
      if (!active) return;
      const fieldSpec = parseRecommendationFields(active.collects).find(
        (f) => f.name === field,
      );
      composer.addFiles(files, {
        tag: attachmentTag(active.recommendationId, field),
        tagLabel: label ?? fieldSpec?.prompt ?? field.replace(/_/g, " "),
      });
    },
    [active, composer],
  );

  const attachmentsFor = useCallback(
    (field: string): ComposerAttachment[] => {
      if (!active) return [];
      const tag = attachmentTag(active.recommendationId, field);
      return attachments.filter((a) => a.tag === tag);
    },
    [active, attachments],
  );

  const removeAttachment = useCallback(
    (id: string) => composer.removeAttachment(id),
    [composer],
  );

  const prefillPrompt = useCallback(
    (rec: Recommendation) => {
      // Populate the chat bar — the user edits and sends it themselves —
      // and pin the chip's id on the composer: the send carries it in
      // additional_kwargs.metadata however the text is edited. The id,
      // not the text, is what the backend resolves.
      setActive(null);
      composer.setText(rec.prompt ?? rec.text);
      composer.setPendingMetadata({ recommendation_id: rec.recommendationId });
      // Focus the chat input so the user can edit/send immediately.
      requestAnimationFrame(() => {
        const input = document.querySelector<HTMLTextAreaElement>(
          '[data-testid="chat-input"]',
        );
        input?.focus();
        input?.setSelectionRange(input.value.length, input.value.length);
      });
    },
    [composer],
  );

  // Attachments that belong to THIS submission: untagged (the note's) +
  // tagged under the active recommendation. Other panels' tagged
  // attachments stay in the composer untouched.
  const scopedAttachments = useCallback((): {
    completed: CompletedAttachment[];
    anyUploading: boolean;
  } => {
    if (!active) return { completed: [], anyUploading: false };
    const prefix = attachmentTagPrefix(active.recommendationId);
    const mine = attachments.filter((a) => !a.tag || a.tag.startsWith(prefix));
    return {
      completed: mine
        .filter((a) => a.status === "completed" && a.url)
        .map((a) => ({
          url: a.url!,
          mimeType: a.mimeType || "application/octet-stream",
          filename: a.filename,
          meta: a.meta as Record<string, unknown> | undefined,
          tag: a.tag,
          tagLabel: a.tagLabel,
        })),
      anyUploading: mine.some((a) => a.status === "uploading"),
    };
  }, [active, attachments]);

  const { completed: completedScoped, anyUploading } = scopedAttachments();

  const requiredFieldsFilled = fields.every((f) => {
    if (f.optional) return true;
    if (f.accepts === "image") {
      const tag = active ? attachmentTag(active.recommendationId, f.name) : "";
      return completedScoped.some((a) => a.tag === tag);
    }
    const v = collected[f.name];
    if (f.fields) {
      // Group: at least one record with every required subfield filled.
      return (
        Array.isArray(v) &&
        v.some(
          (record) =>
            record !== null &&
            typeof record === "object" &&
            f.fields!.every((sub) => {
              if (sub.optional) return true;
              const text = (record as Record<string, unknown>)[sub.name];
              return typeof text === "string" && text.trim() !== "";
            }),
        )
      );
    }
    return typeof v === "string" && v.trim() !== "";
  });

  const bespokeSubmittable = active
    ? descriptorFor(active)?.isSubmittable?.(collected) ?? true
    : true;

  const isSubmittable =
    Boolean(active) &&
    (requiredFieldsFilled || noteText.trim() !== "") &&
    bespokeSubmittable &&
    !anyUploading;

  const submitActive = useCallback(() => {
    if (!active) return;
    const recId = active.recommendationId;
    const prefix = attachmentTagPrefix(recId);
    const snapshot = composer.getSnapshot();
    const mine = snapshot.attachments.filter(
      (a) => !a.tag || a.tag.startsWith(prefix),
    );
    if (mine.some((a) => a.status === "uploading")) return;

    const message = buildRecommendationSubmission({
      recommendation: active,
      collected: collectedByRec[recId] ?? {},
      note: snapshot.text,
      attachments: mine
        .filter((a) => a.status === "completed" && a.url)
        .map((a) => ({
          url: a.url!,
          mimeType: a.mimeType || "application/octet-stream",
          filename: a.filename,
          meta: a.meta as Record<string, unknown> | undefined,
          tag: a.tag,
          tagLabel: a.tagLabel,
        })),
    });

    send({ messages: [message], error: null });

    // Consume what we sent: the note text + this submission's attachments.
    // Other panels' tagged attachments survive.
    composer.setText("");
    composer.removeAttachments((a) => !a.tag || Boolean(a.tag?.startsWith(prefix)));
    setCollectedByRec((prev) => {
      const next = { ...prev };
      delete next[recId];
      return next;
    });
    setActive(null); // close the panel
  }, [active, composer, collectedByRec, send]);

  const value = useMemo<RecommendationsContextValue>(
    () => ({
      recommendations,
      renderers,
      defaultSubmission,
      active,
      open,
      close,
      cancel,
      prefillPrompt,
      submitActive,
      isSubmittable,
      descriptorFor,
      iconFor,
      hasPanel,
      isStreaming,
      fields,
      collected,
      setField,
      addImages,
      attachmentsFor,
      removeAttachment,
      submitLabel,
      notePlaceholder,
    }),
    [
      recommendations,
      renderers,
      active,
      open,
      close,
      cancel,
      prefillPrompt,
      submitActive,
      isSubmittable,
      descriptorFor,
      iconFor,
      hasPanel,
      isStreaming,
      fields,
      collected,
      setField,
      addImages,
      attachmentsFor,
      removeAttachment,
      submitLabel,
      notePlaceholder,
    ],
  );

  return (
    <RecommendationsContext.Provider value={value}>
      {children}
    </RecommendationsContext.Provider>
  );
}
